"use client";

import { IconCircle, IconSparkles } from "@tabler/icons-react";
import { useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import MultidietBigIcon from "@/assets/images/multidiet-big.png";
import MultidietCateringIcon from "@/assets/images/multidiet-cat.png";
import { Img } from "@/components/image";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { Badge } from "@/components/ui/badge";
import { Button, buttonVariants } from "@/components/ui/button";
import { MultidietCtaButton } from "@/features/static-builder/components/multidiet-cta/multidiet-cta-button";
import { cn } from "@/lib/utils";
import type { MultidietCTAListingNewType } from "../../types/multidiet-cta.type";

export const MultidietCTAListingNew = ({
  data,
}: {
  data: MultidietCTAListingNewType | null;
}) => {
  const searchParams = useSearchParams();
  const t = useTranslations();
  const tagsFromUrl = useMemo(() => {
    const tags = searchParams.get("tagsSlugs");
    return tags ? tags.split(",") : [];
  }, [searchParams]);

  if (!data) {
    return null;
  }

  return (
    <div
      className="relative flex w-full flex-col rounded-lg border border-brand-grey2 bg-card text-card-foreground shadow-none md:flex-row"
      data-pw="multidiet-banner-new"
    >
      <div className="relative flex justify-center bg-brand-grey2 p-3 max-md:rounded-t-lg md:w-48 md:rounded-l-lg xl:h-44 xl:w-44">
        <div className="rounded-full bg-brand-grey6 p-3">
          <Img
            src={MultidietBigIcon.src}
            alt="Multidiet"
            width={130}
            height={130}
            className="max-md:max-h-28 max-md:max-w-28"
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col bg-brand-grey1 p-4 xl:py-6">
        <div className="flex gap-4">
          <ListingItemCateringImage
            className="absolute top-2 left-2 bg-brand-grey1 md:static"
            imageUrl={MultidietCateringIcon.src}
            name="Multidieta"
            url={"#"}
          />
          <div className="flex flex-col gap-1">
            <div className="flex flex-nowrap gap-1.5">
              <BadgeNew />
              <BadgeMultidiet tags={tagsFromUrl} />
            </div>
            <header className="w-full">
              <h3
                className="flex flex-wrap items-center font-medium text-xl md:mb-0.5 md:text-2xl"
                data-pw="diet-header"
              >
                <p className="group relative mr-2 items-center text-left transition duration-100">
                  {data.title}
                </p>

                <IconCircle
                  stroke={1.25}
                  className="mr-2 inline-block h-1.5 w-1.5 rounded-full border border-black"
                />

                <p className="group relative items-center transition duration-100">
                  {data.subtitle}
                </p>
              </h3>
            </header>
          </div>
        </div>
        <div className="mt-2 flex flex-col items-center gap-4 md:mt-4 md:flex-row">
          {data.description && (
            <div
              className="flex-1 text-sm md:line-clamp-2"
              dangerouslySetInnerHTML={{ __html: data.description }}
            />
          )}
          <MultidietCtaButton
            testId="multidiet-banner-button"
            className="h-12 min-h-0 rounded-lg px-0 font-medium md:h-10 md:px-5 xl:h-12"
          >
            <Button
              className={cn(
                buttonVariants({ variant: "secondary" }),
                "h-12 min-h-0 w-full rounded-lg px-0 font-medium md:h-10 md:w-auto md:px-5 xl:h-12",
              )}
              data-pw="configure-diet-button"
            >
              {t("multidiet.order")}
            </Button>
          </MultidietCtaButton>
        </div>
      </div>
    </div>
  );
};

const BadgeNew = () => {
  const t = useTranslations();
  return (
    <Badge
      className="order-5 rounded-full border-0 bg-brand-violet-bg px-3 py-2 text-brand-violet-dark md:px-3 md:py-1.5"
      variant="outline"
    >
      <IconSparkles className="mr-1 h-4 w-4" />
      <p className="flex items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-sm leading-4 md:text-[11px]">
        {t("diets.badges.new")}
      </p>
    </Badge>
  );
};

const BadgeMultidiet = ({ tags }: { tags: string[] }) => {
  const t = useTranslations();

  if (!tags || tags.length === 0) {
    return null;
  }

  let label = t(`dietTags.${tags[0]}`);

  if (tags.length >= 2) {
    label = t("diets.badges.multidiet");
  }

  if (tags.includes("CUSTOM_CHOICE")) {
    label = t("dietTags.CUSTOM_CHOICE");
  }
  return (
    <Badge
      className="order-5 rounded-full border-0 bg-white px-3 py-2 text-brand-brown md:px-3 md:py-1.5"
      variant="outline"
    >
      <p className="flex items-center gap-1 overflow-hidden text-ellipsis whitespace-nowrap font-medium text-sm leading-4 md:text-[11px]">
        {label}
      </p>
    </Badge>
  );
};
